import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Grid } from '@material-ui/core'
import { v4 as uuidv4 } from 'uuid'
import { useQueryClient } from 'react-query'
import { faro } from '@grafana/faro-web-sdk'
import { isFloat, htmlToString, isQuizStarted } from '../../utils'
import {
  useQuestionPutQuery,
  useQuestionPostQuery,
} from '../../Queries/QuestionQuery'
import {
  useGetAllQuizzesForAQuestion,
  useUpdateQuestionForQuizzes,
} from '../../Queries/QuizQuery'
import ConfirmBox from '../common/ConfirmBox'
import { useStyles } from './AddCodingQuestionStyles'
import EditCodingQuestion from './EditCodingQuestion'
import CodingQuestionTile from './CodingQuestionTile'

const QUESTION_TYPE = 'coding'

const ALLOWED_INPUT_TYPES = [
  'integer',
  'double',
  'string',
  'integer array',
  'double array',
  'string array',
]

const createDefaultTestCases = () => [
  { id: `Added${uuidv4()}`, type: 'sample', input: '', output: '', marks: 0 },
  { id: `Added${uuidv4()}`, type: 'hidden', input: '', output: '', marks: 5 },
]

const cloneQuestion = (q) => JSON.parse(JSON.stringify(q))

const removeAddedIds = (dummyQuestion) => {
  const modified = dummyQuestion?.testcases?.map(({ id, ...rest }) => {
    if (typeof id === 'string' && id.includes('Added')) return { ...rest }
    return { id, ...rest }
  })
  return { ...dummyQuestion, testcases: modified }
}

/**
 * Normalize inputTypes to always be an array of { name, type } objects.
 * Handles backward-compat where old data might be a flat string[].
 */
const normalizeInputTypes = (raw) => {
  if (!Array.isArray(raw)) return []
  return raw.map((item) =>
    typeof item === 'string' ? { name: '', type: item } : item
  )
}

function AddCodingQuestion({
  question,
  questionIndex,
  addQuestion,
  setAddQuestion,
  setCancelAddQuestion,
  reload,
  setReload,
  quizId,
  quizStartTime,
  isPublished,
  save,
  setSave,
  setTruthValue,
  setErrorMessage,
  setErrors,
  errors,
  questionTypeFromParent,
  handleQuestionTypeChange,
  questions,
  refetchGetQueryById,
  refetchGetQuizQuestionQueryById,
  postQuestionMapping,
  setQuestionType,
  deleteMappingQuestion,
  deleteQuestion,
  invalidTestCaseInput,
  invalidTestCaseArguments,
  setInvalidTestCaseArguments,
  setInvalidTestCaseInput,
  isWriteAllowed,
  setLocalQuestion,
  localQuestion,
  setTextEditorQuestion,
  textEditorQuestion,
  setEditViewQuestion,
  editViewQuestion,
  deleteQuestionId,
  setDeleteQuestionId,
  editOngoing,
  setEditOngoing,
  editQuestion,
  setEditQuestion,
}) {
  const queryClient = useQueryClient()
  const classes = useStyles()

  const [anchorEl, setAnchorEl] = useState(null)
  const [openDialog, setOpenDialog] = useState(false)
  const [affectedQuizes, setAffectedQuizes] = useState([])
  const [selectedQuizzes, setSelectedQuizzes] = useState([])
  const [showQuizesListModal, setShowQuizesListModal] = useState(false)

  const isEditMode = editQuestion === questionIndex || addQuestion

  const handleOpenMenu = (event) => setAnchorEl(event.currentTarget)
  const handleCloseMenu = () => setAnchorEl(null)

  const testCaseCounts = useMemo(() => {
    const testcases = localQuestion?.testcases || []
    return {
      sample: testcases.filter((tc) => tc?.type?.toLowerCase() === 'sample')
        .length,
      hidden: testcases.filter((tc) => tc?.type?.toLowerCase() === 'hidden')
        .length,
    }
  }, [localQuestion?.testcases])

  const hasRequiredTestCases =
    testCaseCounts.sample >= 1 && testCaseCounts.hidden >= 1

  // Return type bool drives the output placeholder in EditTestCase
  const isBoolReturnType = localQuestion?.codeStubConfigs?.returnType === 'bool'
  const outputPlaceholder = isBoolReturnType
    ? 'e.g. 1 (true) or 0 (false)'
    : undefined

  const calculateCodingMarks = useCallback(() => {
    const targetQuestion =
      Object.keys(localQuestion || {}).length > 0 ? localQuestion : question
    if (!targetQuestion) return 0
    return (
      targetQuestion?.testcases
        ?.map((a) => a?.marks ?? 0)
        ?.reduce((acc, curr) => acc + curr, 0) || 0
    )
  }, [localQuestion, question])

  const editQuestionQuery = useQuestionPutQuery(
    quizId,
    localQuestion?.id,
    QUESTION_TYPE,
    {
      staleTime: 0,
      onSuccess: () => {
        setEditQuestion(NaN)
        refetchGetQueryById()
        refetchGetQuizQuestionQueryById()
        setErrorMessage('Question Saved Successfully!')
        setTruthValue(true)
        setReload(reload + 1)
        setLocalQuestion({})
        setEditViewQuestion({})
        setTextEditorQuestion({ question: '<p></p>' })
      },
      onError: () => {
        setErrorMessage('Failed')
      },
    }
  )

  const postQuestion = useQuestionPostQuery({
    staleTime: 0,
    onSuccess: (successResponse) => {
      setQuestionType('coding')
      const id = successResponse?.data?.data?.id
      postQuestionMapping.mutate({ questionId: id, type: localQuestion?.type })
      queryClient.invalidateQueries('quizGetQueryById')
      setLocalQuestion({})
    },
    onError: () => {
      setErrorMessage('Failed')
    },
  })

  const updateQuestionsInQuizzes = useUpdateQuestionForQuizzes({
    onSuccess: () => {
      setEditQuestion(NaN)
      setShowQuizesListModal(false)
      setErrorMessage('Question Saved Successfully!')
      setTruthValue(true)
      setAddQuestion(false)
      setReload(reload + 1)
      refetchGetQueryById()
      refetchGetQuizQuestionQueryById()
      queryClient.invalidateQueries('quizGetQueryById')
    },
  })

  const postNewQuestion = useQuestionPostQuery({
    staleTime: 0,
    retry: false,
    onSuccess: (successResponse) => {
      const newQuestionId = successResponse?.data?.data?.id
      updateQuestionsInQuizzes.mutate({
        oldQuestionId: localQuestion?.id,
        newQuestionId,
        type: localQuestion?.type,
        marks: localQuestion?.marks,
        quizIDs: selectedQuizzes.map((quiz) => quiz?.quizID),
      })
    },
  })

  const { refetch: refetchQuizzesForQuestion } = useGetAllQuizzesForAQuestion(
    localQuestion?.id,
    {
      enabled: false,
      onSuccess: (response) => {
        const filteredQuizzes = response?.filter(
          (quiz) => quiz?.status === 'DRAFT' || quiz?.status === 'UPCOMING'
        )
        setAffectedQuizes(filteredQuizzes || [])
      },
    }
  )

  useEffect(() => {
    if (!Number.isNaN(editQuestion)) {
      refetchQuizzesForQuestion()
    }
  }, [editQuestion, refetchQuizzesForQuestion])

  useEffect(() => {
    if (question && !addQuestion) return

    const isExistingCodingQuestion =
      editViewQuestion?.id !== undefined && editViewQuestion?.type === 'coding'

    const value = {
      id: isExistingCodingQuestion ? editViewQuestion?.id : uuidv4(),
      question: isExistingCodingQuestion
        ? editViewQuestion?.question
        : '<p></p>',
      type: isExistingCodingQuestion ? editViewQuestion?.type : 'coding',
      testcases: isExistingCodingQuestion
        ? editViewQuestion?.testcases
        : createDefaultTestCases(),
      codeStubConfigs: isExistingCodingQuestion
        ? {
            ...editViewQuestion?.codeStubConfigs,
            inputTypes: normalizeInputTypes(
              editViewQuestion?.codeStubConfigs?.inputTypes
            ),
          }
        : { inputTypes: [] },
      marks: isExistingCodingQuestion
        ? editViewQuestion?.marks
        : calculateCodingMarks(),
    }

    setLocalQuestion(value)
    setTextEditorQuestion((prev) => ({
      ...cloneQuestion(prev),
      question: value?.question,
    }))
    setEditViewQuestion(value)
  }, [question])

  const validateSingle = (currentType, inputValue) => {
    const pushFaroError = (error) => {
      if (faro?.api?.pushError) faro.api.pushError(error)
    }

    if (currentType === 'integer') {
      return (
        /^\d+$/.test(inputValue) &&
        !Number.isNaN(parseInt(inputValue, 10)) &&
        Number.isInteger(parseFloat(inputValue))
      )
    }
    if (currentType === 'integer array') {
      if (inputValue[0] !== '[') return false
      try {
        const parsedArray = JSON.parse(inputValue)
        return parsedArray.every(
          (value) =>
            !Number.isNaN(parseInt(value, 10)) &&
            Number.isInteger(parseFloat(value))
        )
      } catch (error) {
        pushFaroError(error)
        return false
      }
    }
    if (currentType === 'string') {
      return typeof inputValue === 'string' && inputValue.indexOf(',') === -1
    }
    if (currentType === 'string array') {
      try {
        if (inputValue[0] !== '[') return false
        const parsedArray = inputValue
          .replace(/^\[|\]$/g, '')
          .split(',')
          .map((item) => item.trim())
        return parsedArray.every((value) => value.indexOf(',') === -1)
      } catch (error) {
        pushFaroError(error)
        return false
      }
    }
    if (currentType === 'double') return isFloat(inputValue)
    if (currentType === 'double array') {
      if (inputValue[0] !== '[') return false
      try {
        const parsedArray = JSON.parse(inputValue)
        return parsedArray.every((value) => isFloat(value))
      } catch (error) {
        pushFaroError(error)
        return false
      }
    }
    return true
  }

  const validate = (input, test) => {
    for (let index = 0; index < input?.length; index++) {
      if (!ALLOWED_INPUT_TYPES?.includes(input[index])) return false
    }

    const arrayOfVariables = []
    let i = 0
    while (i < test.length) {
      if (test[i] === '[') {
        const arrayEnding = test.indexOf(']', i)
        if (arrayEnding === -1) return false
        const arrayFound = test.slice(i, arrayEnding + 1)
        if (arrayFound) arrayOfVariables.push(arrayFound)
        i += arrayFound.length + 1
      } else {
        const nextComma = test.indexOf(',', i)
        const substr = test.slice(i, nextComma !== -1 ? nextComma : test.length)
        if (substr) arrayOfVariables.push(substr)
        i += substr.length + 1
      }
    }

    if (input.length !== arrayOfVariables.length) return false
    return input.every((inputType, j) =>
      validateSingle(inputType, arrayOfVariables[j])
    )
  }

  const validateEmptyTestCaseField = (dummyLocalQuestion, field) => {
    return (
      dummyLocalQuestion?.testcases?.filter((tc) => tc?.[field] === '').length >
      0
    )
  }

  const checkError = (testCaseInputArray, inputTypeArray, choiceIndex) => {
    if (
      !testCaseInputArray ||
      !inputTypeArray ||
      testCaseInputArray?.length <= 0 ||
      inputTypeArray?.length <= 0 ||
      !validate(inputTypeArray, testCaseInputArray)
    ) {
      return true
    }
    setInvalidTestCaseArguments((preValue) => {
      if (preValue?.includes(choiceIndex)) return [...preValue]
      return [choiceIndex]
    })
    return false
  }

  const weightageValidation = (weightageToBeValidated) => {
    if (!weightageToBeValidated) {
      setErrors((prev) => ({
        ...prev,
        emptyWeightage: true,
        weightageLessThanZero: false,
        weightageNotValid: false,
      }))
    } else {
      setErrors((prev) => ({ ...prev, emptyWeightage: false }))
      if (/^-?\d+\.?\d*$/.test(weightageToBeValidated)) {
        setErrors((prev) => ({
          ...prev,
          weightageNotValid: false,
          weightageLessThanZero: weightageToBeValidated <= 0,
        }))
      } else {
        setErrors((prev) => ({
          ...prev,
          weightageLessThanZero: false,
          weightageNotValid: true,
        }))
      }
    }
  }

  const handleChangeLanguage = (event, newValue) => {
    const dummyQuestion = cloneQuestion(localQuestion)
    dummyQuestion.codeStubConfigs.languages = newValue
    setLocalQuestion(dummyQuestion)
  }

  const handleChangeReturnType = (event) => {
    const dummyQuestion = cloneQuestion(localQuestion)
    dummyQuestion.codeStubConfigs.returnType = event?.target?.value
    setLocalQuestion(dummyQuestion)
  }

  const handleFunctionName = (event) => {
    const value = event?.target?.value
    const hasSpace =
      value?.indexOf(' ') >= 0 ||
      localQuestion?.codeStubConfigs?.functionName?.indexOf(' ') >= 0

    setErrors({
      ...errors,
      emptyFunction: value?.trim() === '',
      invalidFunctionName: hasSpace,
    })

    const dummyQuestion = cloneQuestion(localQuestion)
    dummyQuestion.codeStubConfigs.functionName = value
    setLocalQuestion(dummyQuestion)
  }

  const handleAddInputParam = (type) => {
    if (!type) return
    const dummyQuestion = cloneQuestion(localQuestion)
    const current = normalizeInputTypes(
      dummyQuestion.codeStubConfigs.inputTypes
    )
    if (current.length >= 10) return
    dummyQuestion.codeStubConfigs.inputTypes = [
      ...current,
      { name: `param${current.length + 1}`, type },
    ]
    setErrors({ ...errors, emptyInputTypes: false })
    setLocalQuestion(dummyQuestion)
  }

  const handleRemoveInputParam = (index) => {
    const dummyQuestion = cloneQuestion(localQuestion)
    const current = normalizeInputTypes(
      dummyQuestion.codeStubConfigs.inputTypes
    )
    current.splice(index, 1)
    dummyQuestion.codeStubConfigs.inputTypes = current
    setErrors({ ...errors, emptyInputTypes: current.length === 0 })
    setLocalQuestion(dummyQuestion)
  }

  // test case inputs were only checked when typed, so a mismatch flagged before the
  // parameters were set (or changed) never cleared and kept Save disabled
  const paramsChecked = useRef(false)
  useEffect(() => {
    if (!paramsChecked.current) {
      paramsChecked.current = true
      return
    }
    const types = normalizeInputTypes(
      localQuestion?.codeStubConfigs?.inputTypes
    )
      .map((p) => p.type)
      .filter(Boolean)
    setInvalidTestCaseInput(
      (localQuestion?.testcases || []).reduce(
        (bad, tc, i) =>
          typeof tc.input === 'string' &&
          tc.input &&
          checkError(tc.input, types, i)
            ? [...bad, i]
            : bad,
        []
      )
    )
  }, [localQuestion?.codeStubConfigs?.inputTypes])

  const handleEdit = (event, setting, choiceIndex) => {
    const dummyQuestion = cloneQuestion(localQuestion)
    const inputs = event?.target?.value

    switch (setting) {
      case 'marks':
        dummyQuestion.testcases[choiceIndex].marks = parseInt(inputs, 10)
        break
      case 'questionWeightage':
        weightageValidation(inputs)
        dummyQuestion.marks = inputs
        break
      case 'input': {
        const splittedArr = normalizeInputTypes(
          dummyQuestion?.codeStubConfigs?.inputTypes
        )
          .map((p) => p.type)
          .filter(Boolean)

        const isValid = splittedArr?.every((ele) =>
          ALLOWED_INPUT_TYPES.includes(ele)
        )
        if (isValid) {
          if (checkError(inputs, splittedArr, choiceIndex)) {
            setInvalidTestCaseInput([...invalidTestCaseInput, choiceIndex])
          } else {
            setInvalidTestCaseInput(
              invalidTestCaseInput?.filter((i) => i !== choiceIndex)
            )
          }
        }
        dummyQuestion.testcases[choiceIndex].input = inputs
        break
      }
      case 'output':
        dummyQuestion.testcases[choiceIndex].output = inputs
        break
      case 'codeStubBody':
        dummyQuestion.codeStubs[choiceIndex].body = inputs
        break
      default:
        break
    }

    setLocalQuestion(dummyQuestion)
  }

  const handleChange = (event, testcaseId) => {
    const copyLocalQuestion = cloneQuestion(localQuestion)
    copyLocalQuestion.testcases = copyLocalQuestion?.testcases.map((item) => {
      if (item?.id === testcaseId) {
        const newType = event.target.checked ? 'hidden' : 'sample'
        return { ...item, type: newType, marks: newType === 'hidden' ? 5 : 0 }
      }
      return item
    })
    setLocalQuestion(copyLocalQuestion)
  }

  const handleChoice = (setting) => {
    if (setting !== 'Add') return
    const dummyQuestion = cloneQuestion(localQuestion)
    dummyQuestion.testcases.push({
      id: `Added${uuidv4()}`,
      type: 'sample',
      input: '',
      output: '',
      marks: 0,
    })
    setLocalQuestion(dummyQuestion)
  }

  const handleRemoveChoice = (selectedIndex) => {
    const dummyQuestion = cloneQuestion(localQuestion)
    dummyQuestion.testcases = dummyQuestion?.testcases?.filter(
      (x) => x.id !== selectedIndex
    )
    setLocalQuestion(dummyQuestion)
  }

  const parseTestCaseInput = (test) => {
    const arrayOfVariables = []
    let i = 0
    while (i < test.length) {
      if (test[i] === '[') {
        const arrayEnding = test.indexOf(']', i)
        if (arrayEnding === -1) return false
        const arrayFound = test.slice(i, arrayEnding + 1)
        if (arrayFound) arrayOfVariables.push(arrayFound)
        i += arrayFound.length + 1
      } else {
        const nextComma = test.indexOf(',', i)
        const substr = test.slice(i, nextComma !== -1 ? nextComma : test.length)
        if (substr) arrayOfVariables.push(substr)
        i += substr.length + 1
      }
    }
    return arrayOfVariables
  }

  const handleSave = async (value = '') => {
    const dummyLocalQuestion = cloneQuestion(localQuestion)
    dummyLocalQuestion.question = textEditorQuestion.question

    if (htmlToString(dummyLocalQuestion?.question).trim() === '') {
      setErrorMessage('Question cannot be empty')
      setErrors((prev) => ({ ...prev, emptyQuestion: true }))
      if (addQuestion) setSave(false)
      return
    }
    setErrors((prev) => ({ ...prev, emptyQuestion: false }))

    const { codeStubConfigs } = localQuestion || {}

    if (!codeStubConfigs?.languages?.length) {
      setErrorMessage('Languages cannot be empty')
      return
    }

    const normalizedInputTypes = normalizeInputTypes(
      codeStubConfigs?.inputTypes
    )

    if (normalizedInputTypes.length === 0) {
      setErrorMessage('Input parameters cannot be empty')
      return
    }

    const hasIncompleteParams = normalizedInputTypes.some(
      (p) => !p.type || p.type.trim() === ''
    )
    if (hasIncompleteParams) {
      setErrorMessage('All input parameters must have a type selected')
      return
    }

    if (!codeStubConfigs?.functionName) {
      setErrorMessage('Function cannot be empty')
      return
    }
    if (codeStubConfigs?.functionName.trim() === '') {
      setErrorMessage('Function cannot be empty')
      setErrors((prev) => ({ ...prev, spaceFunctionName: true }))
      return
    }
    setErrors((prev) => ({ ...prev, spaceFunctionName: false }))

    if (!codeStubConfigs?.returnType) {
      setErrorMessage('Return type cannot be empty')
      return
    }

    if (!dummyLocalQuestion?.testcases?.length) {
      setSave(false)
      setErrorMessage('Please add test cases')
      return
    }

    const sampleCount = dummyLocalQuestion?.testcases?.filter(
      (tc) => tc?.type?.toLowerCase() === 'sample'
    ).length
    const hiddenCount = dummyLocalQuestion?.testcases?.filter(
      (tc) => tc?.type?.toLowerCase() === 'hidden'
    ).length

    if (sampleCount === 0) {
      setErrorMessage('Please add at least one sample (visible) test case')
      return
    }
    if (hiddenCount === 0) {
      setErrorMessage('Please add at least one hidden test case')
      return
    }

    const hasEmptyInput = validateEmptyTestCaseField(
      dummyLocalQuestion,
      'input'
    )
    const hasEmptyOutput = validateEmptyTestCaseField(
      dummyLocalQuestion,
      'output'
    )

    if (hasEmptyInput && hasEmptyOutput) {
      setErrorMessage('Please add input and output!')
      return
    }
    if (hasEmptyInput) {
      setSave(false)
      setErrorMessage('Please add input!')
      return
    }
    if (hasEmptyOutput) {
      setSave(false)
      setErrorMessage('Please add output!')
      return
    }

    const modifiedTestCases = dummyLocalQuestion?.testcases?.map((testcase) => {
      const parsed = parseTestCaseInput(testcase.input)
      if (parsed === false) return false
      return {
        ...testcase,
        input: Array.isArray(parsed[0]) ? parsed[0] : parsed,
        marks: parseInt(testcase.marks, 10),
      }
    })

    dummyLocalQuestion.testcases = modifiedTestCases
    dummyLocalQuestion.marks = parseInt(calculateCodingMarks(), 10)
    dummyLocalQuestion.quizID = quizId

    // Serialize back to flat string[] for the API
    if (dummyLocalQuestion?.codeStubConfigs?.inputTypes) {
      dummyLocalQuestion.codeStubConfigs = {
        ...dummyLocalQuestion.codeStubConfigs,
        inputTypes: normalizeInputTypes(
          dummyLocalQuestion.codeStubConfigs.inputTypes
        ).map((p) => p.type),
      }
    }

    if (value === 'duplicateEdit') {
      await postNewQuestion.mutateAsync(removeAddedIds(dummyLocalQuestion))
      setEditOngoing(false)
      return
    }

    if (!addQuestion) {
      if (dummyLocalQuestion.isEditable === false) {
        await deleteMappingQuestion.mutate({
          quizId,
          questionId: dummyLocalQuestion?.id,
        })
        delete dummyLocalQuestion.id
        postQuestion?.mutate(removeAddedIds(dummyLocalQuestion))
      } else {
        editQuestionQuery.mutate(removeAddedIds(dummyLocalQuestion))
      }
    } else {
      delete dummyLocalQuestion.id
      postQuestion?.mutate(removeAddedIds(dummyLocalQuestion))
    }
    setEditOngoing(false)
  }

  useEffect(() => {
    if (save && addQuestion) {
      handleSave()
    }
  }, [save, addQuestion])

  const handleSaveButtonClick = async () => {
    if (affectedQuizes?.length > 1) {
      setShowQuizesListModal(true)
    } else {
      handleSave()
    }
  }

  const updateQuestionInSelectedQuizzes = async () => {
    const dummyLocalQuestion = cloneQuestion(localQuestion)
    dummyLocalQuestion.question = textEditorQuestion.question
    dummyLocalQuestion.quizID = quizId
    delete dummyLocalQuestion.id

    if (dummyLocalQuestion?.codeStubConfigs?.inputTypes) {
      dummyLocalQuestion.codeStubConfigs = {
        ...dummyLocalQuestion.codeStubConfigs,
        inputTypes: normalizeInputTypes(
          dummyLocalQuestion.codeStubConfigs.inputTypes
        ).map((p) => p.type),
      }
    }
    await postNewQuestion.mutateAsync(removeAddedIds(dummyLocalQuestion))
  }

  const handleCancel = () => {
    setEditOngoing(false)
    setEditQuestion(NaN)
  }

  const handleCancelCode = () => {
    setEditOngoing(false)
    setAddQuestion(false)
    setCancelAddQuestion(true)
  }

  const handleDelete = () => {
    setOpenDialog(true)
    setDeleteQuestionId(question?.id)
  }

  const handleConfirmDelete = () => {
    deleteQuestion.mutate({
      quizId,
      questionId: deleteQuestionId,
      questionType: QUESTION_TYPE,
    })
    setOpenDialog(false)
    setDeleteQuestionId(undefined)
  }

  const handleCloseDialog = () => setOpenDialog(false)

  const handleQuestionEdit = () => {
    if (editOngoing) {
      setErrorMessage('Please finish the previous edit')
    } else {
      setEditOngoing(true)
      setLocalQuestion(question)
      setTextEditorQuestion({ question: question?.question })
      setEditQuestion(questionIndex)
    }
  }

  return (
    <Grid item container xs={12}>
      {isEditMode ? (
        <EditCodingQuestion
          question={question}
          addQuestion={addQuestion}
          questions={questions}
          questionIndex={questionIndex}
          quizId={quizId}
          localQuestion={localQuestion}
          textEditorQuestion={textEditorQuestion}
          setTextEditorQuestion={setTextEditorQuestion}
          errors={errors}
          isQuizStarted={isQuizStarted}
          quizStartTime={quizStartTime}
          isPublished={isPublished}
          questionTypeFromParent={questionTypeFromParent}
          handleQuestionTypeChange={handleQuestionTypeChange}
          handleEdit={handleEdit}
          handleChange={handleChange}
          handleChangeLanguage={handleChangeLanguage}
          handleChangeReturnType={handleChangeReturnType}
          handleFunctionName={handleFunctionName}
          handleAddInputParam={handleAddInputParam}
          handleRemoveInputParam={handleRemoveInputParam}
          handleChoice={handleChoice}
          handleRemoveChoice={handleRemoveChoice}
          handleSave={handleSave}
          handleCancel={handleCancel}
          handleCancelCode={handleCancelCode}
          handleSaveButtonClick={handleSaveButtonClick}
          calculateCodingMarks={calculateCodingMarks}
          normalizeInputTypes={normalizeInputTypes}
          anchorEl={anchorEl}
          handleOpenMenu={handleOpenMenu}
          handleCloseMenu={handleCloseMenu}
          editQuestionQuery={editQuestionQuery}
          postQuestion={postQuestion}
          showQuizesListModal={showQuizesListModal}
          setShowQuizesListModal={setShowQuizesListModal}
          affectedQuizes={affectedQuizes}
          updateQuestionInSelectedQuizzes={updateQuestionInSelectedQuizzes}
          setSelectedQuizzes={setSelectedQuizzes}
          selectedQuizzes={selectedQuizzes}
          invalidTestCaseInput={invalidTestCaseInput}
          invalidTestCaseArguments={invalidTestCaseArguments}
          editQuestion={editQuestion}
          testCaseCounts={testCaseCounts}
          hasRequiredTestCases={hasRequiredTestCases}
          outputPlaceholder={outputPlaceholder}
        />
      ) : (
        <CodingQuestionTile
          question={question}
          questionIndex={questionIndex}
          editQuestion={editQuestion}
          addQuestion={addQuestion}
          isPublished={isPublished}
          isQuizStarted={isQuizStarted}
          quizStartTime={quizStartTime}
          deleteQuestion={deleteQuestion}
          handleDelete={handleDelete}
          isWriteAllowed={isWriteAllowed}
          handleQuestionEdit={handleQuestionEdit}
          calculateCodingMarks={calculateCodingMarks}
        />
      )}

      <ConfirmBox
        isOpenDialog={openDialog}
        handleClose={handleCloseDialog}
        handleClick={handleConfirmDelete}
        boxDescription="Are you sure you want to delete this question?"
        styles={classes}
        buttonName="Delete"
      />
    </Grid>
  )
}

export default AddCodingQuestion
